import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import axios from "axios";

import {
  FiUsers,
  FiUserCheck,
  FiClipboard,
  FiTrendingUp,
  FiTrendingDown,
  FiMapPin,
  FiFilter,
  FiRefreshCw,
  FiBarChart2,
} from "react-icons/fi";
import { FiDownload } from "react-icons/fi";

import {
  fetchDashboardStats,
  fetchBeneficiaryStats,
  fetchSurveyStats,
} from "../../redux/actions/admin-dashboard/AdminDashboardActions";
import {
  fetchDistricts,
  fetchMunicipalities,
  clearMunicipalities,
} from "../../redux/actions/internal/internalActions";

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    loading,
    dashboardStats,
    beneficiaryStats,
    surveyStats,
    beneficiarySummary,
    beneficiaryDemographics,
  } = useSelector(
    (state) =>
      state.adminDashboard || {
        loading: false,
        dashboardStats: {
          totalSurveyOfficers: 0,
          totalBeneficiaries: 0,
          totalBeneficiarySurveys: 0,
        },
        beneficiaryStats: [],
        surveyStats: [],
        beneficiarySummary: {},
        beneficiaryDemographics: {},
      },
  );

  const { districts, municipalities } = useSelector(
    (state) =>
      state.internal || {
        districts: [],
        municipalities: [],
      },
  );

  useEffect(() => {
    dispatch(fetchDistricts());
  }, [dispatch]);

  useEffect(() => {
    if (selectedDistrict) {
      dispatch(fetchMunicipalities(selectedDistrict));
    } else {
      dispatch(clearMunicipalities());
      setSelectedMunicipality("");
    }
  }, [selectedDistrict, dispatch]);

  useEffect(() => {
    fetchAllData();
  }, [selectedDistrict, selectedMunicipality]);

  const fetchAllData = async () => {
    setIsRefreshing(true);
    await Promise.all([
      dispatch(fetchDashboardStats()),
      dispatch(
        fetchBeneficiaryStats(
          selectedDistrict || null,
          selectedMunicipality || null,
        ),
      ),
      dispatch(fetchSurveyStats()),
    ]);
    setIsRefreshing(false);
  };

  const handleDistrictChange = (e) => {
    setSelectedDistrict(e.target.value);
    setSelectedMunicipality("");
  };

  const handleMunicipalityChange = (e) => {
    setSelectedMunicipality(e.target.value);
  };

  const handleRefresh = () => {
    fetchAllData();
  };

  // ---------- Export: Eligible ----------
  const handleExportEligible = async () => {
    try {
      const toastId = toast.loading("Fetching Eligible records...");
      const token = localStorage.getItem("token");

      let queryParams = `?page=1&limit=200000&eligibility_status=Eligible`;
      if (selectedDistrict) queryParams += `&district_id=${selectedDistrict}`;
      if (selectedMunicipality) queryParams += `&municipality_id=${selectedMunicipality}`;

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/api/admin/beneficiary-surveys${queryParams}`,
        { headers: { authorization: token } },
      );

      const allData = response.data?.data?.data || response.data?.data || [];
      if (allData.length === 0) {
        toast.dismiss(toastId);
        toast.error("No Eligible records found");
        return;
      }

      toast.loading(`Exporting ${allData.length} Eligible records...`, { id: toastId });

      const headers = [
        "Sl. no.", "ID", "Applicant Name", "Address", "Pincode", "Scheme", "Age",
        "Gender", "Caste", "Father's Name", "Mobile No.", "District", "Municipality",
        "Ward", "Resident of ULB", "Constituency", "Survey Officer Name",
        "Survey Officer ID", "Survey Officer Phone", "DLS Status", "Part No.",
        "Serial No.", "BPL ID", "BPL Member ID", "Sanction Order No.", "Ration Card No.",
        "EPIC No.", "Beneficiary ID.", "Beneficiary No.", "Latitude", "Longitude",
        "Eligibility Status", "Ineligible Reason", "Voter Card Image", "Valid Voter Card",
        "Reason - Voter Card Not Collected", "Aadhar Card Image", "Valid Aadhar Card",
        "Reason - Aadhaar Card Not Collected", "Receives other Govt. Pension",
        "Other Govt. Pension Name", "Geo Tagged Photo Image", "Comments",
        "DLC expiry date", "Updated Date",
      ];

      const rows = allData.map((item, index) => ({
        "Sl. no.": index + 1,
        ID: item.id || "N/A",
        "Applicant Name": item.applicant_name || "N/A",
        Address: item.address || "N/A",
        Pincode: item.pincode || "N/A",
        Scheme: item.scheme || "N/A",
        Age: item.age || "N/A",
        Gender: item.gender || "N/A",
        Caste: item.caste || "N/A",
        "Father's Name": item.fh_name || "N/A",
        "Mobile No.": item.mobile_no || "N/A",
        District: item.district_name || "N/A",
        Municipality: item.municipality_name || "N/A",
        Ward: item.ward || "N/A",
        "Resident of ULB": item.resident_of_ulb || "N/A",
        Constituency: item.constituency || "N/A",
        "Survey Officer Name": item.survey_officer_name || "N/A",
        "Survey Officer ID": item.survey_officer_id || "N/A",
        "Survey Officer Phone": item.survey_officer_phone || "N/A",
        "DLS Status": item.dls_status || "N/A",
        "Part No.": item.part_no || "N/A",
        "Serial No.": item.srl_no || "N/A",
        "BPL ID": item.bpl_id || "N/A",
        "BPL Member ID": item.bpl_member_id || "N/A",
        "Sanction Order No.": item.sanction_order_no || "N/A",
        "Ration Card No.": item.ration_card_no || "N/A",
        "EPIC No.": item.epic_no || "N/A",
        "Beneficiary ID.": item.beneficiary_id || "N/A",
        "Beneficiary No.": item.beneficiary_no || "N/A",
        Latitude: item.latitude || "N/A",
        Longitude: item.longitude || "N/A",
        "Eligibility Status": item.eligibility_status || "N/A",
        "Ineligible Reason": item.ineligible_reason || "N/A",
        "Voter Card Image": item.voter_card_collected || "N/A",
        "Valid Voter Card": item.valid_voter_card || "N/A",
        "Reason - Voter Card Not Collected": item.no_voter_card_collected_reason || "N/A",
        "Aadhar Card Image": item.aadhar_card_collected || "N/A",
        "Valid Aadhar Card": item.valid_aadhar_card || "N/A",
        "Reason - Aadhaar Card Not Collected": item.no_aadhar_card_collected_reason || "N/A",
        "Receives other Govt. Pension": item.other_govt_pension || "N/A",
        "Other Govt. Pension Name": item.other_govt_pension_name || "N/A",
        "Geo Tagged Photo Image": item.geo_tagged_photo_captured || "N/A",
        Comments: item.specific_comments || "N/A",
        "DLC expiry date": item.dlc_expired_date
          ? new Date(item.dlc_expired_date).toLocaleDateString("en-IN")
          : "N/A",
        "Updated Date": item.updated_at
          ? new Date(item.updated_at).toLocaleDateString("en-IN")
          : "N/A",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Eligible");
      ws["!cols"] = headers.map(() => ({ wch: 20 }));

      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
      XLSX.writeFile(wb, `Eligible_Beneficiaries_${dateStr}.xlsx`);

      toast.success(`Exported ${allData.length} Eligible records!`, { id: toastId });
    } catch (error) {
      toast.error("Failed to export Eligible records");
      console.error(error);
    }
  };

  // ---------- Export: Ineligible ----------
  const handleExportIneligible = async () => {
    try {
      const toastId = toast.loading("Fetching Ineligible records...");
      const token = localStorage.getItem("token");

      let queryParams = `?page=1&limit=200000&eligibility_status=Ineligible`;
      if (selectedDistrict) queryParams += `&district_id=${selectedDistrict}`;
      if (selectedMunicipality) queryParams += `&municipality_id=${selectedMunicipality}`;

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/api/admin/beneficiary-surveys${queryParams}`,
        { headers: { authorization: token } },
      );

      const allData = response.data?.data?.data || response.data?.data || [];
      if (allData.length === 0) {
        toast.dismiss(toastId);
        toast.error("No Ineligible records found");
        return;
      }

      toast.loading(`Exporting ${allData.length} Ineligible records...`, { id: toastId });

      const headers = [
        "Sl. no.", "ID", "Applicant Name", "Address", "Pincode", "Scheme", "Age",
        "Gender", "Caste", "Father's Name", "Mobile No.", "District", "Municipality",
        "Ward", "Resident of ULB", "Constituency", "Survey Officer Name",
        "Survey Officer ID", "Survey Officer Phone", "DLS Status", "Part No.",
        "Serial No.", "BPL ID", "BPL Member ID", "Sanction Order No.", "Ration Card No.",
        "EPIC No.", "Beneficiary ID.", "Beneficiary No.", "Latitude", "Longitude",
        "Eligibility Status", "Ineligible Reason", "Voter Card Image", "Valid Voter Card",
        "Reason - Voter Card Not Collected", "Aadhar Card Image", "Valid Aadhar Card",
        "Reason - Aadhaar Card Not Collected", "Receives other Govt. Pension",
        "Other Govt. Pension Name", "Geo Tagged Photo Image", "Comments",
        "DLC expiry date", "Updated Date",
      ];

      const data = [
        headers,
        ...allData.map((item, index) => [
          index + 1,
          item.id || "N/A",
          item.applicant_name || "N/A",
          item.address || "N/A",
          item.pincode || "N/A",
          item.scheme || "N/A",
          item.age || "N/A",
          item.gender || "N/A",
          item.caste || "N/A",
          item.fh_name || "N/A",
          item.mobile_no || "N/A",
          item.district_name || "N/A",
          item.municipality_name || "N/A",
          item.ward || "N/A",
          item.resident_of_ulb || "N/A",
          item.constituency || "N/A",
          item.survey_officer_name || "N/A",
          item.survey_officer_id || "N/A",
          item.survey_officer_phone || "N/A",
          item.dls_status || "N/A",
          item.part_no || "N/A",
          item.srl_no || "N/A",
          item.bpl_id || "N/A",
          item.bpl_member_id || "N/A",
          item.sanction_order_no || "N/A",
          item.ration_card_no || "N/A",
          item.epic_no || "N/A",
          item.beneficiary_id || "N/A",
          item.beneficiary_no || "N/A",
          item.latitude || "N/A",
          item.longitude || "N/A",
          item.eligibility_status || "N/A",
          item.ineligible_reason || "N/A",
          item.voter_card_collected || "N/A",
          item.valid_voter_card || "N/A",
          item.no_voter_card_collected_reason || "N/A",
          item.aadhar_card_collected || "N/A",
          item.valid_aadhar_card || "N/A",
          item.no_aadhar_card_collected_reason || "N/A",
          item.other_govt_pension || "N/A",
          item.other_govt_pension_name || "N/A",
          item.geo_tagged_photo_captured || "N/A",
          item.specific_comments || "N/A",
          item.dlc_expired_date
            ? new Date(item.dlc_expired_date).toLocaleDateString("en-IN")
            : "N/A",
          item.updated_at
            ? new Date(item.updated_at).toLocaleDateString("en-IN")
            : "N/A",
        ]),
      ];

      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Ineligible");
      ws["!cols"] = headers.map(() => ({ wch: 20 }));

      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
      XLSX.writeFile(wb, `Ineligible_Beneficiaries_${dateStr}.xlsx`);

      toast.success(`Exported ${allData.length} Ineligible records!`, { id: toastId });
    } catch (error) {
      toast.error("Failed to export Ineligible records");
      console.error(error);
    }
  };

  // ---------- Export: Fit for Deletion ----------
  const handleExportFitForDeletion = async () => {
    try {
      const toastId = toast.loading("Fetching Fit for Deletion records...");
      const token = localStorage.getItem("token");

      let queryParams = `?page=1&limit=200000&eligibility_status=Fit for Deletion`;
      if (selectedDistrict) queryParams += `&district_id=${selectedDistrict}`;
      if (selectedMunicipality) queryParams += `&municipality_id=${selectedMunicipality}`;

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/api/admin/beneficiary-surveys${queryParams}`,
        { headers: { authorization: token } },
      );

      const allData = response.data?.data?.data || response.data?.data || [];
      if (allData.length === 0) {
        toast.dismiss(toastId);
        toast.error("No Fit for Deletion records found");
        return;
      }

      toast.loading(`Exporting ${allData.length} Fit for Deletion records...`, { id: toastId });

      const headers = [
        "Sl. no.", "ID", "Applicant Name", "Address", "Pincode", "Scheme", "Age",
        "Gender", "Caste", "Father's Name", "Mobile No.", "District", "Municipality",
        "Ward", "Resident of ULB", "Constituency", "Survey Officer Name",
        "Survey Officer ID", "Survey Officer Phone", "DLS Status", "Part No.",
        "Serial No.", "BPL ID", "BPL Member ID", "Sanction Order No.", "Ration Card No.",
        "EPIC No.", "Beneficiary ID.", "Beneficiary No.", "Latitude", "Longitude",
        "Eligibility Status", "Ineligible Reason", "Voter Card Image", "Valid Voter Card",
        "Reason - Voter Card Not Collected", "Aadhar Card Image", "Valid Aadhar Card",
        "Reason - Aadhaar Card Not Collected", "Receives other Govt. Pension",
        "Other Govt. Pension Name", "Geo Tagged Photo Image", "Comments",
        "DLC expiry date", "Updated Date",
      ];

      const rows = allData.map((item, index) => ({
        "Sl. no.": index + 1,
        ID: item.id || "N/A",
        "Applicant Name": item.applicant_name || "N/A",
        Address: item.address || "N/A",
        Pincode: item.pincode || "N/A",
        Scheme: item.scheme || "N/A",
        Age: item.age || "N/A",
        Gender: item.gender || "N/A",
        Caste: item.caste || "N/A",
        "Father's Name": item.fh_name || "N/A",
        "Mobile No.": item.mobile_no || "N/A",
        District: item.district_name || "N/A",
        Municipality: item.municipality_name || "N/A",
        Ward: item.ward || "N/A",
        "Resident of ULB": item.resident_of_ulb || "N/A",
        Constituency: item.constituency || "N/A",
        "Survey Officer Name": item.survey_officer_name || "N/A",
        "Survey Officer ID": item.survey_officer_id || "N/A",
        "Survey Officer Phone": item.survey_officer_phone || "N/A",
        "DLS Status": item.dls_status || "N/A",
        "Part No.": item.part_no || "N/A",
        "Serial No.": item.srl_no || "N/A",
        "BPL ID": item.bpl_id || "N/A",
        "BPL Member ID": item.bpl_member_id || "N/A",
        "Sanction Order No.": item.sanction_order_no || "N/A",
        "Ration Card No.": item.ration_card_no || "N/A",
        "EPIC No.": item.epic_no || "N/A",
        "Beneficiary ID.": item.beneficiary_id || "N/A",
        "Beneficiary No.": item.beneficiary_no || "N/A",
        Latitude: item.latitude || "N/A",
        Longitude: item.longitude || "N/A",
        "Eligibility Status": item.eligibility_status || "N/A",
        "Ineligible Reason": item.ineligible_reason || "N/A",
        "Voter Card Image": item.voter_card_collected || "N/A",
        "Valid Voter Card": item.valid_voter_card || "N/A",
        "Reason - Voter Card Not Collected": item.no_voter_card_collected_reason || "N/A",
        "Aadhar Card Image": item.aadhar_card_collected || "N/A",
        "Valid Aadhar Card": item.valid_aadhar_card || "N/A",
        "Reason - Aadhaar Card Not Collected": item.no_aadhar_card_collected_reason || "N/A",
        "Receives other Govt. Pension": item.other_govt_pension || "N/A",
        "Other Govt. Pension Name": item.other_govt_pension_name || "N/A",
        "Geo Tagged Photo Image": item.geo_tagged_photo_captured || "N/A",
        Comments: item.specific_comments || "N/A",
        "DLC expiry date": item.dlc_expired_date
          ? new Date(item.dlc_expired_date).toLocaleDateString("en-IN")
          : "N/A",
        "Updated Date": item.updated_at
          ? new Date(item.updated_at).toLocaleDateString("en-IN")
          : "N/A",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Fit for Deletion");
      ws["!cols"] = headers.map(() => ({ wch: 20 }));

      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
      XLSX.writeFile(wb, `Fit_for_Deletion_Beneficiaries_${dateStr}.xlsx`);

      toast.success(`Exported ${allData.length} Fit for Deletion records!`, { id: toastId });
    } catch (error) {
      toast.error("Failed to export Fit for Deletion records");
      console.error(error);
    }
  };

  // ---------- Export: Beneficiary Statistics table (NEW ORDER) ----------
  const handleExportBeneficiaryStats = async () => {
    try {
      const toastId = toast.loading("Preparing Beneficiary Statistics Excel...");

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Beneficiary Statistics");

      const columns = [
        { header: "Sl. No.", width: 8 },
        { header: "District", width: 20 },
        { header: "Municipality", width: 25 },
        { header: "Total Beneficiary", width: 18 },
        { header: "Total Eligible", width: 15 },
        { header: "Total Ineligible", width: 15 },
        { header: "Fit for Deletion", width: 18 },
        { header: "Survey Completed", width: 18 },
        { header: "Completion %", width: 15 },
        { header: "Survey Remaining", width: 18 },
      ];

      // Header row
      const headerRow = worksheet.addRow(columns.map(col => col.header));
      headerRow.height = 25;
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 12 };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF4F81BD" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Data rows (arrays)
      beneficiaryStats.forEach((item, index) => {
        const rowArray = [
          index + 1,
          item.district_name || "N/A",
          item.municipality_name || "N/A",
          item.total_beneficiaries,
          parseInt(item.total_eligible) || 0,
          parseInt(item.total_ineligible) || 0,
          parseInt(item.total_fit_for_deletion) || 0,
          item.total_survey_completed,
          item.completion_percentage !== null ? `${item.completion_percentage}%` : "N/A",
          item.total_remaining,
        ];
        const row = worksheet.addRow(rowArray);
        row.eachCell((cell) => {
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          if (index % 2 === 1) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF2F7FB" },
            };
          }
        });
      });

      worksheet.columns = columns.map(col => ({ width: col.width }));
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${date.getFullYear()}`;
      saveAs(blob, `Beneficiary_Statistics_${dateStr}.xlsx`);

      toast.success("Beneficiary Statistics exported successfully!", { id: toastId });
    } catch (error) {
      toast.error("Failed to export Beneficiary Statistics");
      console.error(error);
    }
  };

  const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444"];

  // StatCard component
  const StatCard = ({ title, value, icon: Icon, color, subtitle, trend }) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-[0_5px_25px_rgba(0,0,0,0.22)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.18)] transition-all duration-300 hover:scale-105">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-2">
            {title}
            {trend && (
              <span className={`text-xs font-semibold ${trend > 0 ? "text-green-500" : "text-red-500"}`}>
                {trend > 0 ? <FiTrendingUp className="inline" /> : <FiTrendingDown className="inline" />}
                {Math.abs(trend)}%
              </span>
            )}
          </p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
            {typeof value === "number" ? value.toLocaleString() : value}
          </p>
          {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl ${color} shadow-[0_4px_12px_rgba(0,0,0,0.25)]`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold uppercase text-gray-900 dark:text-white flex items-center gap-3">
            Dashboard Overview
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Comprehensive survey statistics and analytics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg flex items-center gap-2 transition-colors"
          >
            <FiRefreshCw className={`${isRefreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Total Survey Officers"
          value={dashboardStats.totalSurveyOfficers || 0}
          icon={FiUsers}
          color="bg-gradient-to-br from-blue-500 to-blue-600"
          subtitle="Active officers"
        />
        <StatCard
          title="Total Beneficiaries"
          value={dashboardStats.totalBeneficiaries || 0}
          icon={FiUserCheck}
          color="bg-gradient-to-br from-green-500 to-green-600"
          subtitle="All beneficiaries"
        />
        <StatCard
          title="Surveys Completed"
          value={dashboardStats.totalBeneficiarySurveys || 0}
          icon={FiClipboard}
          color="bg-gradient-to-br from-orange-500 to-orange-600"
          subtitle="Total surveys"
        />
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-[0_5px_25px_rgba(0,0,0,0.22)]">
        <div className="flex items-center gap-2 mb-4">
          <FiFilter className="text-orange-600 dark:text-orange-400" />
          <h3 className="font-semibold text-gray-900 dark:text-white">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FiMapPin className="inline mr-1" /> District
            </label>
            <select
              value={selectedDistrict}
              onChange={handleDistrictChange}
              className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white transition-all"
            >
              <option value="">All Districts</option>
              {districts.map((district) => (
                <option key={district.id} value={district.id}>{district.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <FiMapPin className="inline mr-1" /> Municipality
            </label>
            <select
              value={selectedMunicipality}
              onChange={handleMunicipalityChange}
              className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 dark:text-white transition-all disabled:opacity-50"
              disabled={!selectedDistrict}
            >
              <option value="">All Municipalities</option>
              {municipalities.map((municipality) => (
                <option key={municipality.id} value={municipality.id}>{municipality.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-12 h-12 border-4 border-blue-600 dark:border-blue-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : (
        <>
          {/* Beneficiary Statistics Summary */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-[0_5px_25px_rgba(0,0,0,0.22)] mb-8 overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <FiBarChart2 className="text-orange-600" />
                Beneficiary Statistics Overview
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Summary of beneficiary survey progress and coverage
              </p>
            </div>

            <div className="p-6">
              {/* Main Metrics - 4 columns */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-pink-50 to-pink-100 dark:from-pink-900/20 dark:to-pink-800/20 rounded-lg p-4 border border-pink-200 dark:border-pink-800">
                  <p className="text-xs font-medium text-pink-600 dark:text-pink-400 uppercase tracking-wider">Total Beneficiaries</p>
                  <p className="text-2xl font-bold text-pink-700 dark:text-pink-300 mt-1">
                    {beneficiarySummary.total_beneficiaries?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
                  <p className="text-xs font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wider">Surveys Completed</p>
                  <p className="text-2xl font-bold text-orange-700 dark:text-orange-300 mt-1">
                    {beneficiarySummary.total_survey_completed?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
                  <p className="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wider">Remaining Surveys</p>
                  <p className="text-2xl font-bold text-red-700 dark:text-red-300 mt-1">
                    {beneficiarySummary.total_remaining?.toLocaleString() || 0}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                  <p className="text-xs font-medium text-purple-600 dark:text-purple-400 uppercase tracking-wider">Completion Rate</p>
                  <p className="text-2xl font-bold text-purple-700 dark:text-purple-300 mt-1">
                    {beneficiarySummary.completion_rate || 0}%
                  </p>
                </div>
              </div>

              {/* Coverage Metrics - 5 columns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                  <p className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider">Total Districts</p>
                  <div className="flex items-end justify-between mt-1">
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                      {beneficiarySummary.coverage?.total_districts || 0}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">districts</p>
                  </div>
                  <div className="w-full bg-blue-200 dark:bg-blue-800/50 h-1.5 rounded-full mt-2">
                    <div className="bg-blue-600 dark:bg-blue-400 h-1.5 rounded-full transition-all duration-500" style={{ width: "100%" }} />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 rounded-lg p-4 border border-emerald-200 dark:border-emerald-800">
                  <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Total Municipalities</p>
                  <div className="flex items-end justify-between mt-1">
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                      {beneficiarySummary.coverage?.total_municipalities || 0}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">municipalities</p>
                  </div>
                  <div className="w-full bg-emerald-200 dark:bg-emerald-800/50 h-1.5 rounded-full mt-2">
                    <div className="bg-emerald-600 dark:bg-emerald-400 h-1.5 rounded-full transition-all duration-500" style={{ width: "100%" }} />
                  </div>
                </div>

                {/* Total Eligible card */}
                <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/20 dark:to-cyan-800/20 rounded-lg p-4 border border-cyan-200 dark:border-cyan-800">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-cyan-600 dark:text-cyan-400 uppercase tracking-wider">Total Eligible</p>
                    <button onClick={handleExportEligible} className="px-3 py-1 text-xs font-medium bg-cyan-600 hover:bg-cyan-700 text-white rounded-md transition-colors flex items-center gap-1.5" title="Export Eligible beneficiaries">
                      <FiDownload className="w-3.5 h-3.5" /> Export
                    </button>
                  </div>
                  <div className="flex items-end justify-between mt-1">
                    <p className="text-2xl font-bold text-cyan-700 dark:text-cyan-300">
                      {beneficiarySummary.coverage?.total_Eligible || 0}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">beneficiaries</p>
                  </div>
                  <div className="w-full bg-cyan-200 dark:bg-cyan-800/50 h-1.5 rounded-full mt-2">
                    <div className="bg-cyan-600 dark:bg-cyan-400 h-1.5 rounded-full transition-all duration-500" style={{ width: `${beneficiarySummary.total_beneficiaries ? ((beneficiarySummary.coverage?.total_Eligible / beneficiarySummary.total_beneficiaries) * 100) : 0}%` }} />
                  </div>
                </div>

                {/* Total Ineligible card */}
                <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wider">Total Ineligible</p>
                    <button onClick={handleExportIneligible} className="px-3 py-1 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors flex items-center gap-1.5" title="Export Ineligible beneficiaries">
                      <FiDownload className="w-3.5 h-3.5" /> Export
                    </button>
                  </div>
                  <div className="flex items-end justify-between mt-1">
                    <p className="text-2xl font-bold text-red-700 dark:text-red-300">
                      {beneficiarySummary.coverage?.total_Ineligible || 0}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">beneficiaries</p>
                  </div>
                  <div className="w-full bg-red-200 dark:bg-red-800/50 h-1.5 rounded-full mt-2">
                    <div className="bg-red-600 dark:bg-red-400 h-1.5 rounded-full transition-all duration-500" style={{ width: `${beneficiarySummary.total_beneficiaries ? ((beneficiarySummary.coverage?.total_Ineligible / beneficiarySummary.total_beneficiaries) * 100) : 0}%` }} />
                  </div>
                </div>

                {/* Fit for Deletion card */}
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wider">Fit for Deletion</p>
                    <button onClick={handleExportFitForDeletion} className="px-3 py-1 text-xs font-medium bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors flex items-center gap-1.5" title="Export Fit for Deletion beneficiaries">
                      <FiDownload className="w-3.5 h-3.5" /> Export
                    </button>
                  </div>
                  <div className="flex items-end justify-between mt-1">
                    <p className="text-2xl font-bold text-orange-700 dark:text-orange-300">
                      {beneficiarySummary.coverage?.total_Fit_for_Deletion || 0}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">beneficiaries</p>
                  </div>
                  <div className="w-full bg-orange-200 dark:bg-orange-800/50 h-1.5 rounded-full mt-2">
                    <div className="bg-orange-600 dark:bg-orange-400 h-1.5 rounded-full transition-all duration-500" style={{ width: `${beneficiarySummary.total_beneficiaries ? ((beneficiarySummary.coverage?.total_Fit_for_Deletion / beneficiarySummary.total_beneficiaries) * 100) : 0}%` }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* BENEFICIARY STATISTICS TABLE (NEW COLUMN ORDER) */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-[0_5px_25px_rgba(0,0,0,0.22)] border border-gray-200 dark:border-gray-700 mb-8 overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold uppercase text-gray-900 dark:text-white flex items-center gap-2">
                    <FiMapPin className="text-orange-600" />
                    Beneficiary Statistics
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Showing {beneficiaryStats.length} municipalities
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Completion: <span className="font-bold text-green-600">{beneficiarySummary.completion_rate || 0}%</span>
                  </span>
                  <button
                    onClick={handleExportBeneficiaryStats}
                    className="px-4 py-2 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-all shadow-md hover:shadow-lg"
                    title="Export Beneficiary Statistics to Excel"
                  >
                    <FiDownload className="w-4 h-4" />
                    Export
                  </button>
                </div>
              </div>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-orange-50 dark:bg-orange-900/80 bg-gradient-to-r from-orange-400/20 to-yellow-400/20 dark:from-orange-500/20 dark:to-yellow-500/20">
                    <th className="text-left py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">#</th>
                    <th className="text-left py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">District</th>
                    <th className="text-left py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">Municipality</th>
                    <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">Total Beneficiary</th>
                    <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">Total Eligible</th>
                    <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">Total Ineligible</th>
                    <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">Fit for Deletion</th>
                    <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">Survey Completed</th>
                    <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">Completion %</th>
                    <th className="text-right py-2.5 px-3 text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 border-b-2 border-gray-300 dark:border-gray-600">Survey Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {beneficiaryStats.length > 0 ? (
                    beneficiaryStats.map((item, index) => (
                      <tr
                        key={index}
                        className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                      >
                        <td className="py-2 px-3 text-sm text-gray-500 dark:text-gray-400">{index + 1}</td>
                        <td className="py-2 px-3 text-sm text-gray-700 dark:text-gray-300 border-r border-gray-100 dark:border-gray-700">{item.district_name}</td>
                        <td className="py-2 px-3 text-sm font-medium text-gray-900 dark:text-white border-r border-gray-100 dark:border-gray-700">{item.municipality_name}</td>
                        <td className="py-2 px-3 text-sm font-medium text-gray-900 dark:text-white text-right border-r border-gray-100 dark:border-gray-700">{item.total_beneficiaries}</td>
                        <td className="py-2 px-3 text-sm font-medium text-cyan-700 dark:text-cyan-300 text-right border-r border-gray-100 dark:border-gray-700">{item.total_eligible || 0}</td>
                        <td className="py-2 px-3 text-sm font-medium text-red-700 dark:text-red-300 text-right border-r border-gray-100 dark:border-gray-700">{item.total_ineligible || 0}</td>
                        <td className="py-2 px-3 text-sm font-medium text-orange-700 dark:text-orange-300 text-right border-r border-gray-100 dark:border-gray-700">{item.total_fit_for_deletion || 0}</td>
                        <td className="py-2 px-3 text-sm font-medium text-green-600 dark:text-green-400 text-right border-r border-gray-100 dark:border-gray-700">{item.total_survey_completed}</td>
                        <td className="py-2 px-3 text-right border-r border-gray-100 dark:border-gray-700">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${item.completion_percentage !== null && parseFloat(item.completion_percentage) > 0 ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"}`}>
                            {item.completion_percentage !== null ? `${item.completion_percentage}%` : "N/A"}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-sm font-medium text-red-600 dark:text-red-400 text-right border-r border-gray-100 dark:border-gray-700">{item.total_remaining}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="py-8 text-center text-gray-500 dark:text-gray-400">
                        <FiClipboard className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        No beneficiary statistics available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Survey Distribution (unchanged) */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-[0_5px_25px_rgba(0,0,0,0.22)] border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold uppercase text-gray-900 dark:text-white flex items-center gap-2">
                  <FiClipboard className="text-orange-600" />
                  Survey Distribution by Municipality
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Total surveys conducted across municipalities
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Total Surveys</span>
              </div>
            </div>

            {(() => {
              const sortedStats = [...(surveyStats || [])].sort((a, b) => b.total_surveys - a.total_surveys);
              const maxSurveys = sortedStats.length > 0 ? Math.max(...sortedStats.map(s => s.total_surveys)) : 1;

              if (sortedStats.length === 0) {
                return (
                  <div className="text-center py-16 text-gray-500 dark:text-gray-400">
                    <FiClipboard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No municipality data available</p>
                  </div>
                );
              }

              return (
                <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                  {sortedStats.map((item) => {
                    const isZero = item.total_surveys === 0;
                    const percentage = isZero ? 1.5 : (item.total_surveys / maxSurveys) * 100;

                    return (
                      <div key={item.municipality_id} className="group space-y-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/20 p-2 rounded-lg transition-colors">
                        <div className="flex items-baseline justify-between text-sm">
                          <div className="flex items-center gap-1.5 truncate">
                            <span className={`font-semibold truncate ${isZero ? "text-gray-500 dark:text-gray-400" : "text-gray-800 dark:text-gray-200"}`}>
                              {item.municipality_name}
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-500 truncate">({item.district_name})</span>
                          </div>
                          <span className={`font-bold flex-shrink-0 pl-2 ${isZero ? "text-gray-400 dark:text-gray-500" : "text-orange-600 dark:text-orange-400"}`}>
                            {item.total_surveys}
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 dark:bg-gray-700/60 h-2.5 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ease-out ${isZero ? "bg-orange-300/40 dark:bg-orange-950/40" : "bg-gradient-to-r from-orange-400 to-orange-600 group-hover:from-orange-500 group-hover:to-orange-700"}`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;